import type * as THREE from 'three';
import { FeatureCurve } from '../FeatureCurve';
import { FeatureLine } from '../FeatureLine';

/**
 * 拓扑边代理创建输入。
 */
export interface CreateTopologyFeatureLineProxyInput {
  /** 边代理几何。 */
  geometry: THREE.BufferGeometry;
  /** 底层曲线类型；缺失时表示当前快照没有解析到解析曲线语义。 */
  curveKind?: TopologyCurveKind;
  /** 当前边代理使用的采样点数量。 */
  sampledPointCount: number;
}

/**
 * 判断当前拓扑边是否必须暴露为解析曲线对象。
 *
 * 设计说明：
 * 1. `FeatureLine` 是直线边的标准交互对象，后续配合和沿实体移动会按直线读取方向；
 * 2. `FeatureCurve` 只代表圆、椭圆、样条等非直线解析曲线；
 * 3. curveKind 缺失但采样点超过两个时，说明当前边至少不能安全当成直线，因此按未知曲线处理。
 *
 * @param input 边代理创建输入。
 * @returns 是否需要创建 `FeatureCurve`。
 */
function shouldCreateFeatureCurve(input: CreateTopologyFeatureLineProxyInput): boolean {
  if (typeof input.curveKind === 'string') {
    return input.curveKind !== 'line';
  }
  return input.sampledPointCount > 2;
}

/**
 * 根据权威拓扑边语义创建特征线代理。
 *
 * @param input 边代理创建输入。
 * @returns 直线返回 `FeatureLine`，非直线解析曲线返回 `FeatureCurve`。
 */
export function createTopologyFeatureLineProxy(input: CreateTopologyFeatureLineProxyInput): FeatureLine {
  if (shouldCreateFeatureCurve(input)) {
    return new FeatureCurve({
      geometry: input.geometry,
      curveKind: input.curveKind,
    });
  }

  /*
   * 直线必须保持为基础 FeatureLine。
   * 这能避免直线被曲线过滤逻辑排除，同时保留曲线对象对圆弧/样条等高级参考的表达能力。
   */
  return new FeatureLine(input.geometry);
}
